<template>
  <Page title="TIX Delisting Notice">
    <v-row no-gutters justify="center" class="mb-16" style="max-width: 100% !important">
      <v-col no-gutters>
        <div :class="[contentFont, spacing]" class="reflective-card">

          <p class="published">Published June 6, 2026 · This page is the only official source for this notice. Verify the URL and the channels listed below before acting on anything you read elsewhere.</p>

          <p>TIX is being delisted from MEXC. If you hold TIX on MEXC, you must withdraw it to a wallet you control before the withdrawal window closes. This page explains the timeline, how to withdraw safely, and how to avoid the scams that follow events like this.</p>

          <h3 :class="sectionSubheadlineFont" class="my-3">Timeline</h3>
          <ul class="priv-list">
            <li>Trading halts: expected June 9 (MEXC processes delistings in batches, so the exact time is not fixed — typically around 14:00 UTC).</li>
            <li>Withdrawals: remain open for approximately 30 days after delisting (until roughly July 9), as long as MEXC continues to support the network.</li>
          </ul>
          <p>Please do not wait for the deadline — withdraw early.</p>

          <h3 :class="sectionSubheadlineFont" class="my-3">How to withdraw your TIX</h3>
          <p>On MEXC, open the withdrawal page for TIX and send to a wallet you control. MEXC withdrawals for TIX are on BNB Smart Chain (BEP20).</p>
          <p class="bold">Send only on BNB Smart Chain (BEP20). Sending on any other network can permanently and irreversibly lose your tokens.</p>

          <a href="https://bscscan.com/token/0x8459AB8d6aa488716c568896c0502C0AF0EB6983" target="_blank">
            <div class="ca">0x8459AB8d6aa488716c568896c0502C0AF0EB6983</div>
          </a>
          <a class="highlight" @click="event => copyToClipboard(event, '0x8459AB8d6aa488716c568896c0502C0AF0EB6983')">
            <div class="ma-2">Copy</div>
          </a>

          <p>A wallet such as MetaMask, configured for BNB Smart Chain, works fine. After the withdrawal window closes, recovering tokens left on the exchange may require a manual support request to MEXC and cannot be guaranteed — please do not leave it to chance.</p>

          <h3 :class="sectionSubheadlineFont" class="my-3">Stay safe — please read</h3>
          <div class="warn">
            <p>Delistings attract scammers and impersonators. Protect yourself:</p>
            <ul class="priv-list">
              <li>The team will <span class="bold">never</span> DM you first.</li>
              <li>We will <span class="bold">never</span> send a "claim," "migration," or "swap" link. You withdraw directly on MEXC.</li>
              <li>We will <span class="bold">never</span> ask for your seed phrase, private keys, or wallet password.</li>
              <li>The <span class="bold">only</span> correct contract address is the one on this page. Ignore any address or instructions posted in replies, DMs, or by other accounts — even ones using our name.</li>
            </ul>
          </div>

          <h3 :class="sectionSubheadlineFont" class="my-3">Why this is happening</h3>
          <p>Maintaining the liquidity and market-making required to support the MEXC listing is no longer a sustainable use of our treasury under current market conditions. Rather than continue spending limited funds on secondary-market costs, we are directing our remaining resources toward building the product.</p>
          <p>We do not currently have the treasury to seed a decentralized (DEX) liquidity pool, so on-chain trading will be limited for the time being. TIX remains what it was designed to be — a utility token for our data processing platform — and its role is tied to real usage of that platform.</p>

          <h3 :class="sectionSubheadlineFont" class="my-3">What's next</h3>
          <p>We are not winding down the project; we are changing how we operate. From here, our communications will focus on development and product milestones rather than market activity. We would rather show progress than talk about price.</p>

          <h3 :class="sectionSubheadlineFont" class="my-3">Official channels</h3>
          <p>These are our only official channels. Anything outside this list is not us:</p>
          <ul class="priv-list">
            <li>Website: <a class="highlight" href="https://timeworx.io/" target="_blank">https://timeworx.io/</a></li>
            <li>Telegram: <a class="highlight" href="https://t.me/timeworx" target="_blank">[link]</a></li>
            <li>Discord: <a class="highlight" href="https://discord.gg/4t2ZCCY5zu" target="_blank">[link]</a></li>
            <li>X / Twitter: <a class="highlight" href="https://twitter.com/timeworx_io" target="_blank">[@timeworx_io]</a></li>
          </ul>

        </div>
      </v-col>
      <v-snackbar v-model="snackbar" timeout="2000">
        {{ snackbarMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="#ff6200" text v-bind="attrs" @click="snackbar = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-row>
  </Page>
</template>

<script>
import Page from "../Page.vue";

export default {
  name: "DelistingNotice",
  metaInfo: {
    title: "TIX Delisting Notice"
  },
  components: { Page },
  props: [],
  data() {
    return {
      snackbar: false,
      snackbarMessage: "",
    };
  },
  computed: {
    contentFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
          return "poppins-normal-white-18px";
        case "md":
        case "sm":
          if (!this.$isMobile()) {
            return "poppins-normal-white-16px";
          } else {
            return "poppins-normal-white-15px";
          }
        case "xs":
          return "poppins-normal-white-15px";
      }
    },
    subtitleFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
          return "poppins-bold-white-42px";
        case "md":
        case "sm":
        case "xs":
          return "poppins-bold-white-24px";
      }
    },
    headlineFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
          return "poppins-bold-white-64px";
        case "md":
        case "sm":
        case "xs":
          return "poppins-bold-white-32px";
      }
    },
    sectionSubheadlineFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "poppins-bold-white-26px";
        case "sm":
        case "xs":
          return "poppins-bold-white-20px";
      }
    },
    spacing() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "spacing-desktop";
        case "sm":
        case "xs":
          return "spacing-mobile";
      }
    },
    xSpacing() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "x-spacing-desktop";
        case "sm":
        case "xs":
          return "x-spacing-mobile";
      }
    },
  },
  methods: {
    copyToClipboard(event, address) {
      event.preventDefault();
      navigator.clipboard.writeText(address).then(() => {
        if (!this.$isMobile()) {
          this.snackbarMessage = `Copied ${address} to your clipboard`;
          this.snackbar = true;
        }
      }, () => {
        this.snackbarMessage = "Failed to copy to clipboard.";
        this.snackbar = true;
      });
    },
  },
};
</script>

<style scoped>
.priv-list {
  list-style-type: none;
  padding-left: 0;
}

.priv-list li {
  margin-bottom: 0.6em;
}

.priv-list li:before {
 content: "\2022";
 padding-right: 0.5em;
 color: white;
}

.highlight {
  color: #B09EFF !important;
  font-weight: 600 !important;
}

.published {
  opacity: 0.7;
  font-style: italic;
  margin-bottom: 1.5em;
}

.spacing-desktop {
  padding: 80px 160px;
  margin-bottom: 80px;
}

.spacing-mobile {
  padding: 20px;
  margin-bottom: 40px;
}

.spacing-last {
  margin-bottom: 0px !important;
}

.x-spacing-desktop {
  padding: 0px 160px;
  margin-bottom: 40px !important;
}

.x-spacing-mobile {
  padding: 0px 20px;
  margin-bottom: 20px !important;
}

* a {
  color: inherit !important;
  text-decoration: none !important;
}

.bold {
  font-weight: 700;
}

.warn {
  border: thin solid #ff6200 !important;
  border-radius: 10px;
  padding: 16px 24px;
  margin: 16px 0;
}

.ca {
  font-family: monospace;
  padding: 10px;
  border: thin solid white !important;
  border-radius: 10px;
  width: 100%;
  overflow-wrap: break-word;
}
</style>